import { Icon } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Table } from '@/components_deprecated'
import { PlainLink } from '@/components_deprecated/Link'

import type { ShallowUserNode } from '@/modules/users'
import styled from 'styled-components'
import { Color, SettingsColorPicker } from '../common'
import type {
  IssueCategoriesSettingsForm,
  IssueCategoryForSettings,
} from './types'
import React from 'react'

const ResponsibleUser = styled(PlainLink)`
  display: flex;
  align-items: center;

  span {
    margin-left: 8px;

    &:hover {
      color: ${props => props.theme.colors.secondary};
    }
  }
`

const CaseHandler = styled(PlainLink)`
  display: flex;
  align-items: center;

  span {
    margin-left: 8px;

    &:hover {
      color: ${props => props.theme.colors.secondary};
    }
  }
`

const SubCategoryContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  flex-direction: row;
`

const SubCategory = styled.span<{ background: string }>`
  padding: 4px 12px;
  border-radius: 40px;
  width: fit-content;
  background: ${props => props.background};
`

interface IssueCategorySettingsTableProps {
  categories: IssueCategoryForSettings[]

  form: IssueCategoriesSettingsForm

  temporaryEditColor: {
    [id: string]: string | null
  }

  onSelectResponsibleUser: (
    categoryId: string | null,
    currentUser: ShallowUserNode | null
  ) => void
  onSelectCaseHandler: (
    categoryId: string | null,
    currentUser: ShallowUserNode | null
  ) => void
  onUpdateColor: (categoryId: string) => (color: string) => void
  onEditClick?: (category: IssueCategoryForSettings) => void
  onDelete: (category: IssueCategoryForSettings) => void
}

export const IssueCategorySettingsTable: React.FC<
  IssueCategorySettingsTableProps
> = ({
  categories,

  form,

  temporaryEditColor,

  onUpdateColor,
  onEditClick,
  onDelete,
}) => {
  const translations = useTranslate({
    name: 'common.name',
    responsibleUser: 'common.responsible-user',
    caseHandler: 'common.case-handler',
    choose: 'common.choose-ellipsis',

    staticCategories: 'settings.static-categories',
    staticCategoriesInfo: 'settings.info.issue-categories-uneditable',
    reminder: 'common.reminder',
  })

  function handleEditClick(category: IssueCategoryForSettings) {
    onEditClick?.(category)
  }

  return (
    <Table
      noShadow
      noBorders
      noHeaderMargin
      noRowMargin
      rowHoverColor="secondary"
    >
      <thead>
        <tr>
          <th></th>
          <th>{translations.name}</th>
          <th>{translations.responsibleUser}</th>
          <th>{translations.caseHandler}</th>
          <th colSpan={3}>{translations.reminder}</th>
        </tr>
      </thead>

      <tbody>
        {categories.map(category => (
          <React.Fragment key={category.id}>
            <tr>
              <td width="1px">
                <Color
                  color={temporaryEditColor[category.id] ?? category.color}
                >
                  <SettingsColorPicker
                    value={temporaryEditColor[category.id] ?? category.color}
                    onChange={onUpdateColor(category.id)}
                  />
                </Color>
              </td>

              <td>{category.name}</td>

              <td>
                <ResponsibleUser
                  to={
                    category.responsibleUser
                      ? `/users/${category.responsibleUser.id}`
                      : '#'
                  }
                >
                  <span>
                    {form.responsibleUser?.fullName ??
                      category.responsibleUser?.fullName}
                  </span>
                </ResponsibleUser>
              </td>

              <td>
                <CaseHandler
                  to={
                    category.caseHandler
                      ? `/users/${category.caseHandler.id}`
                      : '#'
                  }
                >
                  <span>
                    {form.caseHandler?.fullName ??
                      category.caseHandler?.fullName}
                  </span>
                </CaseHandler>
              </td>

              <td width="1px">
                {category.automaticReminderInterval ? (
                  <Icon icon="bell" color="gray6" />
                ) : (
                  ''
                )}
              </td>

              <td width="1px">
                <Icon
                  icon="edit"
                  cursor="pointer"
                  color="gray6"
                  hoverColor="secondary"
                  onClick={() => handleEditClick(category)}
                />
              </td>

              <td width="1px">
                <Icon
                  icon="times"
                  cursor="pointer"
                  color="gray6"
                  hoverColor="red"
                  onClick={() => onDelete(category)}
                />
              </td>
            </tr>
            <tr>
              <td colSpan={7}>
                <SubCategoryContainer>
                  {category?.issueSubcategories?.edges?.map(subcategory => (
                    <SubCategory
                      key={subcategory.node.id}
                      background={
                        temporaryEditColor[category.id] ?? category.color
                      }
                    >
                      {subcategory.node.name}
                    </SubCategory>
                  ))}
                </SubCategoryContainer>
              </td>
            </tr>
          </React.Fragment>
        ))}
      </tbody>
    </Table>
  )
}
